import {useQueryClient} from '../react-query';
import {
  InferConfigType,
  InferFinalResponseType,
  InferResponseType,
} from '../types';
import {generateCacheKey} from '../useRequest';
import {CacheKeyConfig, createCacheKey} from './createCacheKey';
import {
  GrpcQueryConfigWithSection,
  GrpcRequestServiceMethod,
} from './useGenericGrpcRequest';

export const useAttachOnInitialDataSuccessCallback = <
  ServiceMethod extends GrpcRequestServiceMethod,
  MethodName extends ServiceMethod['method'],
  GenericConfig,
  ResponseType extends InferResponseType<
    ReturnType<Awaited<ReturnType<ServiceMethod>>>
  >,
  ParamsType extends Omit<
    Parameters<ServiceMethod>[1],
    keyof Partial<GenericConfig>
  >,
  DefaultConfigType extends GrpcQueryConfigWithSection<
    ResponseType,
    ParamsType
  >,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
  TData extends object = InferFinalResponseType<GrpcConfigType, ResponseType>,
>({
  onRequestInitialDataSuccess,
  uniqueIdentifier,
  queryConfig,
  initialCacheKey,
}: {
  queryConfig: Pick<DefaultConfigType, 'onInitialDataSuccess'>;
  onRequestInitialDataSuccess?: (
    data: TData,
    options: {
      duplicateInitialDataByParams: (params: ParamsType) => void;
      invalidateCache: () => void;
    },
  ) => void;
  transformCacheKey: DefaultConfigType['transformCacheKey'];
  uniqueIdentifier?: string;
  initialCacheKey: string;
}) => {
  const queryClient = useQueryClient();

  if (onRequestInitialDataSuccess) {
    queryConfig.onInitialDataSuccess = initialData => {
      onRequestInitialDataSuccess(initialData, {
        duplicateInitialDataByParams(updatedParams) {
          const cacheKeyConfig = {
            uniqueIdentifier,
            params: updatedParams,
          } as CacheKeyConfig;

          const key = createCacheKey(cacheKeyConfig);

          queryClient.setQueryData(generateCacheKey(key), initialData);
        },
        invalidateCache: () => {
          // setTimeout before calling removeQueries was necessary to remove the cache entry successfully
          // otherwise the cache entry was still available but in an inactive state, which becomes active again after the cachekey becomes active again.
          setTimeout(() => {
            queryClient.removeQuery(generateCacheKey(initialCacheKey));
          });
        },
      });
    };
  }
};
